import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, FileText, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_FILTERS = [
  { key: '', label: 'All' },
  { key: 'leave', label: 'Leave Requests' },
  { key: 'regularization', label: 'Attendance Regularization' },
  { key: 'compoff', label: 'Comp-Off' },
  { key: 'on_duty', label: 'On Duty' },
  { key: 'wfh', label: 'Work From Home' },
  { key: 'kyc', label: 'KYC Requests' },
  { key: 'claim_sheet', label: 'Expense Claims' },
  { key: 'reimbursement', label: 'Reimbursements' },
  { key: 'advance', label: 'Advances' },
  { key: 'profile_change', label: 'Profile Change' },
];

const STATUS_CHIP = {
  pending: 'bg-amber-100 text-amber-700',
  pending_hr: 'bg-orange-100 text-orange-700',
  in_progress: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-600',
  rework: 'bg-purple-100 text-purple-700',
  sent_back: 'bg-purple-100 text-purple-700',
};

const fmtAmt = (v) => (v === null || v === undefined || v === '') ? ''
  : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export const MyRequestsList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/ess/my-requests`, { withCredentials: true });
        setRows(Array.isArray(res.data) ? res.data : []);
      } catch (e) { toast.error('Failed to load your requests'); }
      setLoading(false);
    })();
  }, []);

  const filtered = typeFilter ? rows.filter((r) => r.type === typeFilter) : rows;

  return (
    <div data-testid="my-requests-list" className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {TYPE_FILTERS.map((t) => (
          <button key={t.key} data-testid={`myreq-type-${t.key || 'all'}`}
            onClick={() => setTypeFilter(t.key)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${
              typeFilter === t.key
                ? 'bg-[#0052FF] text-white border-[#0052FF]'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-slate-400" data-testid="myreq-empty">
            <FileText className="w-8 h-8 mb-2" />
            <p className="text-sm">No requests yet — use the Quick Actions above to submit one.</p>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
                <th className="px-4 py-2.5">Request No / Type</th>
                <th className="px-4 py-2.5">Request Details</th>
                <th className="px-4 py-2.5">Submitted / Work Date</th>
                <th className="px-4 py-2.5">Available / Expiry Date</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Approver / Pending With</th>
                <th className="px-4 py-2.5">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={`${r.type}-${r.id}`} data-testid={`myreq-row-${r.type}-${r.id}`}
                  className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                  <td className="px-4 py-3 text-xs font-semibold text-slate-800">
                    <span className="block font-mono text-[10px] text-indigo-600 uppercase">{r.request_no || r.id.slice(0, 8)}</span>
                    {r.label}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {r.type === 'compoff' ? (
                      <span className="font-medium text-slate-800">Comp-Off Request ({r.summary?.work_date || '—'})</span>
                    ) : (
                      [fmtAmt(r.summary?.amount), r.summary?.period, r.summary?.detail, r.title].filter(Boolean).join(' · ') || '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-slate-400" />{r.comp_off_date || (r.created_at || '').slice(0, 10)}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                    {r.type === 'compoff' ? (
                      <span>Avail: {r.available_date || '—'} · Exp: {r.expiry_date || '—'}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap capitalize ${STATUS_CHIP[r.status] || 'bg-slate-100 text-slate-600'}`}>
                      {r.display_status || r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{r.pending_with || 'Reporting Manager'}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 italic max-w-[180px] truncate">{r.remarks || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MyRequestsList;
